import { ReactNode, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import * as Sentry from '@sentry/react';
import { makeStyles } from '@material-ui/core';
import { MDSTheme } from '@marqvision/mds/core';
import { MDSTypography, MDSPlainButton, MDSButton, MDSLogo } from '@marqvision/mds-v2';
import pkg from '../../../package.json';
import { SentryFallbackRenderData } from './@types';

// TODO: same origin / cross origin에 따라 헤더 레이아웃이 꼭 필요한지 다시한번 확인 필요
const useIsSameOrigin = () => {
  const [isSameOrigin, setIsSameOrigin] = useState(false);

  useEffect(() => {
    const currentOrigin = window.location.origin;
    const prevOrigin = document.referrer;
    setIsSameOrigin(prevOrigin.includes(currentOrigin));
  }, []);

  return isSameOrigin;
};

export function CommonErrorPage(props: SentryFallbackRenderData) {
  const isSameOrigin = useIsSameOrigin();

  return isSameOrigin ? (
    <CommonErrorBox {...props} />
  ) : (
    <MarqVisionWrapper>
      <CommonErrorBox {...props} />
    </MarqVisionWrapper>
  );
}

const useStyles = makeStyles(() => ({
  containerLayout: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: MDSTheme.palette.white,
  },
  titleLayout: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    fontFamily: 'Visuelt-Medium', // MDS v1 font가 Visuelt-Regular 로 하드코딩되어있음
    marginBottom: '32px',
    '& h1': {
      margin: 0,
      marginBlock: 0,
    },
    '& h2': {
      margin: 0,
      color: MDSTheme.palette.bluegray[400],
      marginBlock: 0,
    },
  },
  actionLayout: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '16px',
  },
  descriptionLayout: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '4px',
  },
}));

type Props = {
  onRetry?: () => void;
} & Partial<SentryFallbackRenderData>; // Sentry Error boundary로 들어오지 않는 경우에도 이 컴포넌트를 사용하기 위함 (react-query request 결과 error일 때)
export const CommonErrorBox = ({ onRetry, ...errorData }: Props) => {
  const isAvailableToReportSentry = errorData.error && errorData.eventId;

  const classes = useStyles();
  const handleContactUsClick = () => {
    if (isAvailableToReportSentry) {
      Sentry.showReportDialog(errorData as Required<SentryFallbackRenderData>);
    }
  };
  const handleRetryClick = () => {
    if (onRetry) onRetry();
    else {
      window.location.replace(`/?${pkg.version}`);
    }
  };

  return (
    <div className={classes.containerLayout}>
      <div>
        <div className={classes.titleLayout}>
          <MDSTypography variant="title" size="2xl" weight="semibold">
            Something went wrong
          </MDSTypography>
        </div>
        <div className={classes.actionLayout}>
          <MDSButton variant="fill" color="blue" size="large" onClick={handleRetryClick}>
            Try again
          </MDSButton>
        </div>
        {isAvailableToReportSentry && (
          <div className={classes.descriptionLayout}>
            <MDSPlainButton color="blue" size="small" onClick={handleContactUsClick}>
              Contact us
            </MDSPlainButton>
            <MDSTypography variant="body" size="m" weight="regular">
              if this issue recurs.
            </MDSTypography>
          </div>
        )}
      </div>
    </div>
  );
};

const useWrapperStyles = makeStyles(() => ({
  containerLayout: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  header: {
    marginTop: '48px',
  },
  footer: {
    marginBottom: '48px',
  },
}));

const MarqVisionWrapper = ({ children }: { children: ReactNode }) => {
  const classes = useWrapperStyles();
  const currentYear = dayjs().year();
  return (
    <div className={classes.containerLayout}>
      <div className={classes.header}>{<MDSLogo logoType="ai" size={18} color="black" />}</div>
      {children}
      <div className={classes.footer}>
        <MDSTypography variant="body" size="m" weight="regular">
          © {currentYear} Marq Vision Inc. All Rights Reserved.
        </MDSTypography>
      </div>
    </div>
  );
};
